import {
    Box,
    Button,
    FormGroup,
    FormHelperText,
    FormLabel,
    IconButton,
    InputAdornment,
    Modal,
    styled,
    TextField,
    Typography,
    useTheme
} from '@mui/material';
import { StaticDatePicker } from '@mui/x-date-pickers';
import composeRules, { getValidationRules } from '@utils/composeRules';
import { isEmpty } from 'lodash';
import moment, { Moment } from 'moment';
import { useState } from 'react';
import { Controller } from 'react-hook-form';
const StyledModal = styled(Modal)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
}));


const StyledDatePicker = styled(StaticDatePicker)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[8],
}));

type InputDateProps = {
    initialDate?: Date | Moment;
    maxDate?: Date | Moment;
    minDate?: Date | Moment;
    showClear?: boolean;
    format?: string;
    rules?: any;
    control: any;
    name: string;
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    required?: boolean;
    viewOnly?: boolean;
    onChange?: (value: string) => void;
    inputStyle?: React.CSSProperties;
    multiline?: boolean;
    numberOfLines?: number;
    defaultValue?: string;
    helperText?: string;
} ;

const InputDate = (props: InputDateProps) => {
    const [showPicker, setShowPicker] = useState(false);
    const theme = useTheme();
    const rules = composeRules(props.required, props.rules);

    return (
        <FormGroup sx={{ width: '100%' }}>
            <Controller
                control={props.control}
                name={props.name}
                defaultValue={props.defaultValue ?? null}
                rules={{
                    validate: getValidationRules(rules ?? {}),
                }}
                render={({ field: { ref, name, value, onChange }, fieldState: { error } }) => {
                    const handleDatePickerOpen = () => {
                        if (!props.disabled && !props.viewOnly) {
                            setShowPicker(true);
                        }
                    };

                    const handleClearDate = () => {
                        onChange('');
                        props.onChange?.('');
                    };

                    const formatDisplayValue = (dateValue: any) => {
                        if (!dateValue) return '';
                        return moment(dateValue).locale('fr').format(props?.format ?? 'DD/MM/YYYY');
                    };

                    const endAdornment = (
                        <InputAdornment position="end">
                            {props.showClear && !!value && !props.viewOnly && (
                                <IconButton
                                    onClick={handleClearDate}
                                    size="small"
                                    sx={{
                                        marginRight: 0.5,
                                        color: theme.palette.action.active,
                                        '&:hover': {
                                            color: theme.palette.errorRed.main,
                                        },
                                    }}
                                >
                                    <i className="fi fi-br-cross-small" style={{ fontSize: 18 }} />
                                </IconButton>
                            )}
                            <IconButton
                                onClick={handleDatePickerOpen}
                                disabled={props.disabled}
                                size="small"
                                sx={{
                                    color: props.disabled
                                        ? theme.palette.action.disabled
                                        : theme.palette.action.active,
                                }}
                            >
                                <i className="fi fi-br-calendar" style={{ fontSize: 18 }} />
                            </IconButton>
                        </InputAdornment>
                    );

                    return (
                        <>
                            {props.label && (
                                <FormLabel htmlFor={name} error={!!error}>{props.label}</FormLabel>
                            )}
                            {props.helperText && (
                                <FormHelperText>{props.helperText}</FormHelperText>
                            )}
                            {props.viewOnly ? (
                                <Typography
                                    variant="body2"
                                    sx={{ color: theme.palette.text.secondary }}
                                >
                                    {formatDisplayValue(value) || '-'}
                                </Typography>
                            ) : (
                                <TextField
                                    fullWidth
                                    value={formatDisplayValue(value)}
                                    placeholder={props.placeholder}
                                    disabled={props.disabled}
                                    required={props.required}
                                    error={!!error}
                                    helperText={error?.message}
                                    onClick={handleDatePickerOpen}
                                    InputProps={{
                                        readOnly: true,
                                        endAdornment: endAdornment,
                                        sx: {
                                            cursor: 'pointer',
                                        },
                                    }}
                                    sx={{
                                        ...props.inputStyle,
                                    }}
                                />
                            )}

                            <StyledModal
                                open={showPicker}
                                onClose={() => setShowPicker(false)}
                            >
                                <Box>
                                    <StyledDatePicker
                                        views={props?.format === 'MM/YYYY' ? ['year', 'month'] : undefined}
                                        minDate={props?.minDate ? moment(props?.minDate) : undefined}
                                        maxDate={props?.maxDate ? moment(props?.maxDate) : undefined}
                                        value={moment(value)}
                                        slots={{
                                            actionBar: (actionProps) => {
                                                // @ts-ignore
                                                const date = actionProps?.ownerState?.value;
                                                return (
                                                    <Box
                                                        sx={{
                                                            gridColumn: '1 / 4',
                                                            gridRow: '3',
                                                            display: 'flex',
                                                            justifyContent: 'flex-end',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            px: 3,
                                                            pb: 2
                                                        }}
                                                    >
                                                        <Button
                                                            variant="outlined"
                                                            onClick={() => {
                                                                setShowPicker(false);
                                                            }}
                                                            sx={{ color: theme.palette.text.secondary }}
                                                        >
                                                            Annuler
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            onClick={() => {
                                                                setShowPicker(false);
                                                                const formattedDate = !isEmpty(date) ? date.format('YYYY-MM-DD') : '';
                                                                onChange(formattedDate);
                                                                props.onChange?.(formattedDate);
                                                            }}
                                                        >
                                                            OK
                                                        </Button>
                                                    </Box>
                                                );
                                            },
                                        }}
                                    />
                                </Box>
                            </StyledModal>
                        </>
                    );
                }}
            />
        </FormGroup>
    );
};

export default InputDate;
